import React, { useState, useEffect } from "react";
import { Button, message, TimePicker, Select } from "antd";
import dayjs from "dayjs";
import { postRequest, putRequest, getRequest } from "../service/index";
import { getEmployeeId } from "../utils/localStorage";

const AttendanceDetails = ({ onClose, attendanceData }) => {

  const [loading, setLoading] = useState(false);
  const [requiredHours, setRequiredHours] = useState(9); // Default to 9 hours
  const [formData, setFormData] = useState({
    checkIn: attendanceData?.checkIn ? dayjs(attendanceData.checkIn, "HH:mm") : null,
    checkOut: attendanceData?.checkOut ? dayjs(attendanceData.checkOut, "HH:mm") : null,
    workingHour: attendanceData?.workingHour || "",
    overtime: attendanceData?.overtime || "",
    status: attendanceData?.status || ""
  });

  const companyId = getEmployeeId();

  // Fetch shift data to get required hours
  useEffect(() => {
    const fetchShiftData = async () => {
      try {
        const employeeId = attendanceData?.employeeId || attendanceData?.id;
        if (!employeeId) return;

        const response = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
        if (response?.shift) {
          const shift = response.shift;
          // Use workingHours as required hours for overtime calculation (shift duration)
          // This is the actual shift length, so overtime is calculated beyond this
          const reqHours = shift?.workingHours || 9;
          setRequiredHours(reqHours);
        }
      } catch (error) {
        console.error("Error fetching shift data:", error);
        // Keep default 9 hours on error
      }
    };

    if (attendanceData) {
      fetchShiftData();
    }
  }, [attendanceData]);

  // Calculate working hours and overtime
  const calculateHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return { workingHour: "", overtime: "" };
    
    const startTime = dayjs(checkIn);
    const endTime = dayjs(checkOut);
    
    // If end time is before start time, it means next day
    let duration = endTime.diff(startTime, 'minute');
    if (duration < 0) {
      duration += 24 * 60; // Add 24 hours
    }
    
    const totalHours = duration / 60;
    
    // Working hour should always show the actual hours worked
    let workingHour = totalHours;
    let overtime = 0;
    
    // Only calculate overtime if total hours exceed required hours
    if (totalHours > requiredHours) {
      overtime = totalHours - requiredHours;
    }
    
    // Format hours as HH:MM
    const formatHours = (hours) => {
      if (hours === 0) return "00:00";
      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);
      return `${wholeHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };
    
    return {
      workingHour: formatHours(workingHour),
      overtime: formatHours(overtime)
    };
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleTimeChange = (time, field) => {
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [field]: time
      };
      
      // Auto-calculate working hours and overtime when both times are set
      if (newFormData.checkIn && newFormData.checkOut) {
        const calculatedHours = calculateHours(newFormData.checkIn, newFormData.checkOut);
        newFormData.workingHour = calculatedHours.workingHour;
        newFormData.overtime = calculatedHours.overtime;
      } else {
        // Clear calculated fields if one time is missing
        if (field === 'checkIn' || field === 'checkOut') {
          newFormData.workingHour = "";
          newFormData.overtime = "";
        }
      }
      
      return newFormData;
    });
  };

  // Update form data when attendanceData or requiredHours changes
  useEffect(() => {
    if (attendanceData) {
      const checkIn = attendanceData.checkIn ? dayjs(attendanceData.checkIn, "HH:mm") : null;
      const checkOut = attendanceData.checkOut ? dayjs(attendanceData.checkOut, "HH:mm") : null;
      
      // Calculate hours if both times are available
      const calculatedHours = calculateHours(checkIn, checkOut);
      
      setFormData({
        checkIn: checkIn,
        checkOut: checkOut,
        workingHour: calculatedHours.workingHour || attendanceData.workingHour || "",
        overtime: calculatedHours.overtime || attendanceData.overtime || "",
        status: attendanceData.status || ""
      });
    }
  }, [attendanceData, requiredHours]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Convert HH:MM format to decimal hours
      const convertTimeToDecimal = (timeString) => {
        if (!timeString) return 0;
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours + (minutes / 60);
      };
      
      const workingHourNumber = convertTimeToDecimal(formData.workingHour);
      const overtimeNumber = convertTimeToDecimal(formData.overtime);
      
      let response;
      
      // Check if this is an update (existing record) or new record
      if (attendanceData?.checkIn || attendanceData?.checkOut) {
        // Update existing record - use PUT request with specific payload format
        const attendanceId = attendanceData?._id || attendanceData?.id;
        if (!attendanceId) {
          message.error("Attendance record ID not found for update!");
          return;
        }
        
        const updatePayload = {
          checkIn: formData.checkIn ? formData.checkIn.format("HH:mm") : "",
          checkOut: formData.checkOut ? formData.checkOut.format("HH:mm") : "",
          status: formData.status || attendanceData?.status || "absent",
          workingHour: formData.workingHour,
          overtime: formData.overtime
        };
        
        response = await putRequest(`/api/myattendance/${attendanceId}`, updatePayload);

      } else {
        // Create new record - use POST request with full data
        const submitData = {
          ...formData,
          employeeId: attendanceData?.employeeId || attendanceData?.id,
          companyId: companyId,
          checkIn: formData.checkIn ? formData.checkIn.format("HH:mm") : "",
          checkOut: formData.checkOut ? formData.checkOut.format("HH:mm") : "",
          date: attendanceData?.date,
          status: formData.status || attendanceData?.status || "absent",
          workingHour: formData.workingHour,
          overtime: formData.overtime
        };
        
        response = await postRequest("/api/myattendance", submitData);
      }
      
      if (!response.error) {
        const action = attendanceData?.checkIn || attendanceData?.checkOut ? "updated" : "added";
        message.success(`Attendance details ${action} successfully!`);
        onClose();
      } else {
        message.error(response.error || "Failed to save attendance details!");
      }
    } catch (error) {
      message.error("Failed to save attendance details. Please try again.");
      console.error("Error submitting attendance details:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {attendanceData && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-lg mb-2">Employee Information </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="font-medium">Name:</span> {attendanceData.employeeName}</div>
            <div><span className="font-medium">Designation:</span> {attendanceData.designation}</div>
            <div><span className="font-medium">Date:</span> {attendanceData.date}</div>
            <div><span className="font-medium">Status:</span> <span className={`px-2 py-1 rounded text-xs ${
              attendanceData.status === 'Present' ? 'bg-green-100 text-green-800' :
              attendanceData.status === 'Absent' ? 'bg-red-100 text-red-800' :
              attendanceData.status === 'Late' ? 'bg-yellow-100 text-yellow-800' :
              attendanceData.status === 'Late & Early' ? 'bg-orange-100 text-orange-800' :
              attendanceData.status === 'Missing' ? 'bg-purple-100 text-purple-800' :
              attendanceData.status === 'Leave' ? 'bg-blue-100 text-blue-800' :
              attendanceData.status === 'Early' ? 'bg-gray-100 text-gray-800' :
              'bg-gray-100 text-gray-800'
            }`}>{attendanceData.status}</span></div>
            
          </div>
        </div>
      )}
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="checkIn" className="text-md">
            Check In Time
          </label>
          <TimePicker
            format="HH:mm"
            use12Hours={false}
            placeholder="Select check-in time"
            value={formData.checkIn}
            onChange={(time) => handleTimeChange(time, "checkIn")}
            className="w-full"
          />
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="checkOut" className="text-md">
            Check Out Time
          </label>
          <TimePicker
            format="HH:mm"
            use12Hours={false}
            placeholder="Select check-out time"
            value={formData.checkOut}
            onChange={(time) => handleTimeChange(time, "checkOut")}
            className="w-full"
          />
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="workingHour" className="text-md">
            Working Hour
          </label>
          <input
            type="text"
            id="workingHour"
            className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md bg-gray-50"
            placeholder="Auto-calculated from check-in/out times"
            value={formData.workingHour}
            onChange={handleInputChange}
            readOnly
          />
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="overtime" className="text-md">
            Overtime
          </label>
          <input
            type="text"
            id="overtime"
            className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md bg-gray-50"
            placeholder="Auto-calculated from check-in/out times"
            value={formData.overtime}
            onChange={handleInputChange}
            readOnly
          />
        </div>
        <div className="col-span-12 flex flex-col gap-1">
          <label htmlFor="status" className="text-md">
            Status
          </label>
          <Select
            id="status"
            placeholder="Select status"
            value={formData.status}
            onChange={(value) => handleInputChange({ target: { id: 'status', value } })}
            className="w-full"
            options={[
              { value: 'Present', label: 'Present' },
              { value: 'Absent', label: 'Absent' },
              { value: 'Late', label: 'Late' },
              { value: 'Late & Early', label: 'Late & Early' },
              { value: 'Missing', label: 'Missing' },
              { value: 'Leave', label: 'Leave' },
              { value: 'Early', label: 'Early' },
              { value: 'Holiday', label: 'Holiday' },
              { value: 'half', label: 'Half Day' }
            ]}
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 my-2">
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          type="primary"
          className="bg-primary text-white"
        >
          {loading ? "Saving..." : attendanceData?.checkIn || attendanceData?.checkOut ? "Update Attendance" : "Add Attendance"}
        </Button>
      </div>
    </div>
  );
};

export default AttendanceDetails;
